/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import divorceCostsStyle from "./divorceCostsStyle";
import Grid from "@mui/material/Grid";
import CostCalculator from "../../../../services/divorce/CostCalculator";
import FeesService from "../../../../services/FeesService";
import { Divider } from "@mui/material";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";

type DivorceCostsProps = {
  divorce: AbstractCase;
};

const DivorceCosts = ({ divorce }: DivorceCostsProps) => {
  const valueInDispute = CostCalculator.calculateValueInDispute(divorce);
  const installmentRate = (FeesService.getExpenses(true) + CostCalculator.getHearingCost(valueInDispute) + CostCalculator.getProcessCost(valueInDispute)) / 10;

  const CostText = ({ divorce }: { divorce: AbstractCase }) => {
    switch (true) {
      case !!divorce.hasProcessCostSupport:
        return (
          <Grid item xs={12} sx={divorceCostsStyle.postfix}>
            <strong>Sie müssen vorraussichtlich nichts zahlen!</strong>
            <br />
            Wenn Ihr Antrag auf Verfahrenskostenhilfe bewilligt worden ist, sind alle Zahlungen über die Verfahrenskostenhilfe abgedeckt.
          </Grid>
        );

      case !!divorce.installment:
        return (
          <Grid item xs={12} sx={divorceCostsStyle.hintBox}>
            <strong>Sie zahlen bequem per Raten!</strong>
            <br />
            <p>
              Sie haben sich für eine Ratenzahlung entscheiden. Sie zahlen daher die Kosten bequem über die nächsten 10 Monate in 10 Raten zu je{" "}
              {(Math.round(installmentRate * 100) / 100).toLocaleString("de-DE")}€.
            </p>
          </Grid>
        );
      case divorce.acquisitionPartner === "advocard" && divorce.paymentType === "insurance":
        return (
          <Grid item xs={12} sx={divorceCostsStyle.postfix}>
            <strong>Sie sind bei der Advocard versichert?</strong>
            <br />
            Dann übernimmt Ihre Versicherung einen Teil der Kosten für Sie! Kommen Sie bei Fragen hierzu gerne auf uns zu.
          </Grid>
        );
      default:
        return (
          <Grid item xs={12} sx={divorceCostsStyle.postfix}>
            <strong>Fragen zur Finanzierung?</strong>
            <br />
            Melden Sie sich dazu gerne direkt bei uns!
          </Grid>
        );
    }
  };

  const costsSum = divorce.sumOfAllFees + divorce.courtFee;

  return (
    <Grid container sx={divorceCostsStyle.divorceCosts}>
      <Grid item xs={8}>
        Verfahrensgebühr
      </Grid>
      <Grid item xs={4} sx={divorceCostsStyle.money}>
        {divorce.processFeeAndExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      <Grid item xs={8}>
        Terminsgebühr
      </Grid>
      <Grid item xs={4} sx={divorceCostsStyle.money}>
        {divorce.hearingFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      <Grid item xs={8}>
        Gerichtskosten
      </Grid>
      <Grid item xs={4} sx={divorceCostsStyle.money}>
        {divorce.courtFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      <Grid item xs={12}>
        <Divider sx={divorceCostsStyle.divider} />
      </Grid>
      <Grid item xs={8} sx={divorceCostsStyle.sum}>
        Scheidungskosten insgesamt
      </Grid>
      <Grid item xs={4} sx={[divorceCostsStyle.sum, divorceCostsStyle.money]}>
        {costsSum.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      <CostText divorce={divorce} />
    </Grid>
  );
};

export default DivorceCosts;
